import { create } from "zustand"

/**
 * The artifact a thread was started from via "Continue working" (B4).
 * Publishing from that thread proposes "new version of X" (`parent_id`).
 */
export type HostedAgentArtifactSeed = {
  agentSlug: string
  artifactId: string
  rootId: string
  title: string
  version: number
}

type HostedAgentArtifactSeedStore = {
  /**
   * Seed waiting for the NEXT thread the user creates (set by "Continue
   * working", consumed when create-on-send yields a session id).
   */
  pendingSeed: HostedAgentArtifactSeed | null
  /** Seeds bound to created sessions (session id → seed). Session-lifetime, in-memory only. */
  sessionSeeds: Record<string, HostedAgentArtifactSeed>
  /**
   * Text the artifact library asked the composer to show ("Reuse" / "Continue
   * working"). The chat view consumes it once its composer setter is ready.
   */
  pendingComposerText: string | null
  setPendingSeed: (seed: HostedAgentArtifactSeed | null) => void
  bindPendingSeed: (sessionId: string) => void
  setSessionSeed: (sessionId: string, seed: HostedAgentArtifactSeed) => void
  setPendingComposerText: (text: string | null) => void
}

export const useHostedAgentArtifactSeedStore = create<HostedAgentArtifactSeedStore>((set) => ({
  pendingSeed: null,
  sessionSeeds: {},
  pendingComposerText: null,
  setPendingSeed: (seed) => set({ pendingSeed: seed }),
  bindPendingSeed: (sessionId) =>
    set((state) =>
      state.pendingSeed
        ? {
            pendingSeed: null,
            sessionSeeds: { ...state.sessionSeeds, [sessionId]: state.pendingSeed },
          }
        : state
    ),
  setSessionSeed: (sessionId, seed) =>
    set((state) => ({ sessionSeeds: { ...state.sessionSeeds, [sessionId]: seed } })),
  setPendingComposerText: (text) => set({ pendingComposerText: text }),
}))
